import { useState, useEffect, useCallback } from 'react';
import Sidebar from '../components/Sidebar';
import { scheduleApi } from '../api';

type Staff = {
  userIdx: number;
  name: string;
  status: string;
};

interface Room {
  id: string;
  name: string;
  capacity: number;
  status: 'available' | 'occupied' | 'maintenance';
  todayReservationCount?: number;
  nextReservationTime?: string | null;
}

interface Reservation {
  id: string;
  roomId: string;
  roomName: string;
  customerName: string;
  staffIdx: number;
  staffName: string;
  date: string;
  startTime: string;
  endTime: string;
  purpose: string;
  status: 'scheduled' | 'in-progress' | 'completed' | 'cancelled';
}

export default function SelectRooms() {
  const [activeTab, setActiveTab] = useState<'rooms' | 'reservations'>('reservations');

  const [showAddRoomModal, setShowAddRoomModal] = useState(false);
  const [showReservationModal, setShowReservationModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedResDetail, setSelectedResDetail] = useState<Reservation | null>(null);
  const [staffList, setStaffList] = useState<Staff[]>([]);
  const [selectedDate, setSelectedDate] = useState(() => {
    const now = new Date();
    const offset = now.getTimezoneOffset() * 60000;
    return new Date(now.getTime() - offset).toISOString().split('T')[0];
  });

  const [rooms, setRooms] = useState<Room[]>([]);
  const [reservations, setReservations] = useState<Reservation[]>([]);

  const [newRoom, setNewRoom] = useState({ name: '', capacity: 4 });
  const [newReservation, setNewReservation] = useState({
    roomId: '',
    customerName: '',
    customerIdx: 10,
    consultant: '',
    staffIdx: 1,
    date: selectedDate,
    startTime: '10:00',
    endTime: '11:00',
    purpose: '',
    scheduleCategory: 'SELECT',
  });

  const SELECT_CATEGORIES = [
    { value: 'CONSULTATION', label: '상담' },
    { value: 'SELECT', label: '셀렉' },
    { value: 'REHEARSAL_SHOOTING', label: '리허설촬영' },
    { value: 'MAIN_SHOOTING', label: '본식촬영' },
    { value: 'DELIVERY', label: '납품' },
  ];

  const getHour = (timeStr: string) => parseInt(timeStr.split(':')[0], 10);

  const parseTimeFromDt = (dt: string): string => {
    if (!dt) return '00:00';
    const timePart = dt.includes('T') ? dt.split('T')[1] : dt.split(' ')[1];
    return timePart?.substring(0, 5) || '00:00';
  };

  useEffect(() => {
    const fetchStaff = async () => {
      try {
        const token = localStorage.getItem('accessToken');
        const response = await fetch('https://api.wedmin.biz/user?status=ACTIVE&size=100', {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        const result = await response.json();
        if (result?.data?.items) {
          setStaffList(result.data.items);
        }
      } catch (error) {
        console.error('직원 목록 조회 실패:', error);
      }
    };
    fetchStaff();
  }, []);

  const fetchRoomStatus = useCallback(async () => {
    try {
      const data = await scheduleApi.selectRoomGrid(selectedDate);
      if (data.rooms) {
        const mappedRooms: Room[] = data.rooms.map((item: any) => ({
          id: item.roomIdx.toString(),
          name: item.name,
          capacity: item.capacity || 4,
          status: 'available' as const,
          todayReservationCount: data.schedules?.filter((s: any) => s.roomIdx === item.roomIdx).length || 0,
        }));
        setRooms(mappedRooms);
      }
    } catch (error) {
      console.error("Fetch room status error:", error);
    }
  }, [selectedDate]);

  const fetchRoomTimetable = useCallback(async (date: string) => {
    try {
      const data = await scheduleApi.selectRoomGrid(date);
      if (data.rooms) {
        const mappedRooms: Room[] = data.rooms.map((item: any) => ({
          id: item.roomIdx.toString(),
          name: item.name,
          capacity: item.capacity || 4,
          status: 'available' as const,
        }));
        setRooms(mappedRooms);
      }
      const allReservations: Reservation[] = [];
      if (data.schedules) {
        data.schedules.forEach((s: any) => {
          if (!s.roomIdx && !s.room?.roomIdx) return;
          const startDt = new Date(s.startDt);
          const endDt = s.endDt ? new Date(s.endDt) : new Date(startDt.getTime() + 3600000);
          allReservations.push({
            id: (s.photoScheduleIdx || s.scheduleIdx).toString(),
            roomId: (s.room?.roomIdx || s.roomIdx).toString(),
            roomName: s.room?.name || '',
            customerName: s.customerName || s.photoCustomer?.groomName || '예약고객',
            staffIdx: s.assignedStaff?.userIdx || 0,
            staffName: s.assignedStaff?.name || '',
            date,
            startTime: `${String(startDt.getHours()).padStart(2, '0')}:${String(startDt.getMinutes()).padStart(2, '0')}`,
            endTime: `${String(endDt.getHours()).padStart(2, '0')}:${String(endDt.getMinutes()).padStart(2, '0')}`,
            purpose: s.title,
            status: 'scheduled',
          });
        });
      }
      setReservations(allReservations);
    } catch (error) {
      console.error("Fetch timetable error:", error);
      setReservations([]);
    }
  }, []);

  const createRoom = async () => {
    try {
      const token = localStorage.getItem('accessToken');
      const response = await fetch('https://api.wedmin.biz/room', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ name: newRoom.name, capacity: newRoom.capacity, type: "SELECT", isActive: true })
      });
      if (!response.ok) throw new Error('등록 실패');
      const result = await response.json();
      if (result.status) {
        alert("등록되었습니다.");
        setShowAddRoomModal(false);
        setNewRoom({ name: '', capacity: 4 });
        fetchRoomStatus();
      }
    } catch (error) { console.error(error); alert("오류 발생"); }
  };

  const deleteRoom = async (roomIdx: string) => {
    if (!confirm('삭제하시겠습니까?')) return;
    try {
      const token = localStorage.getItem('accessToken');
      const response = await fetch(`https://api.wedmin.biz/room/${roomIdx}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!response.ok) throw new Error('삭제 실패');
      const result = await response.json();
      if (result.status) {
        alert("삭제되었습니다.");
        fetchRoomStatus();
      }
    } catch (error) { console.error(error); alert("오류 발생"); }
  };

  const createReservation = async () => {
    try {
      const startDt = `${newReservation.date}T${newReservation.startTime}:00`;
      const endDt = `${newReservation.date}T${newReservation.endTime}:00`;
      await scheduleApi.create({
        section: 'SELECT',
        scheduleCategory: newReservation.scheduleCategory,
        title: newReservation.purpose || `${newReservation.customerName} ${SELECT_CATEGORIES.find(c => c.value === newReservation.scheduleCategory)?.label || ''}`.trim(),
        startDt, endDt,
        customerName: newReservation.customerName,
        assignedStaffIdx: newReservation.staffIdx || undefined,
        roomIdx: parseInt(newReservation.roomId) || undefined,
      });
      alert("예약되었습니다.");
      setShowReservationModal(false);
      setNewReservation({ ...newReservation, customerName: '', purpose: '' });
      if (activeTab === 'rooms') fetchRoomStatus();
      else fetchRoomTimetable(selectedDate);
    } catch (error: any) { console.error(error); alert(`오류 발생: ${error.message}`); }
  };

  const handleDeleteReservation = async () => {
    if (!selectedResDetail) return;
    if (!confirm('정말 이 예약을 삭제하시겠습니까?')) return;
    try {
      await scheduleApi.delete(Number(selectedResDetail.id));
      alert('예약이 삭제되었습니다.');
      setShowDetailModal(false);
      setSelectedResDetail(null);
      if (activeTab === 'reservations') fetchRoomTimetable(selectedDate);
      else fetchRoomStatus();
    } catch (e: any) { console.error(e); alert(`오류 발생: ${e.message}`); }
  };

  useEffect(() => {
    fetchRoomStatus();
    if (activeTab === 'reservations') fetchRoomTimetable(selectedDate);
  }, [activeTab, selectedDate, fetchRoomStatus, fetchRoomTimetable]);

  const handleAddRoom = () => {
    if (newRoom.name && newRoom.capacity > 0) createRoom();
    else alert("정보를 입력해주세요.");
  };

  const handleAddReservation = () => {
    if (newReservation.roomId && newReservation.customerName) createReservation();
    else alert("셀렉실, 고객명은 필수입니다.");
  };

  const handleReservationClick = (res: Reservation) => {
    setSelectedResDetail(res);
    setShowDetailModal(true);
  };

  const timeSlots = Array.from({ length: 13 }, (_, i) => {
    const hour = i + 9;
    return `${hour.toString().padStart(2, '0')}:00`;
  });

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'available': return 'bg-green-100 text-green-800';
      case 'occupied': return 'bg-red-100 text-red-800';
      case 'maintenance': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getReservationInfo = (roomId: string, currentHour: number) => {
    const reservation = reservations.find(res => {
      if (String(res.roomId) !== String(roomId)) return false;
      const start = getHour(res.startTime);
      const end = getHour(res.endTime);
      return currentHour >= start && currentHour < end;
    });
    if (!reservation) return { type: 'none' };
    const start = getHour(reservation.startTime);
    const end = getHour(reservation.endTime);
    const duration = end - start;
    if (currentHour === start) return { type: 'start', reservation, span: duration };
    return { type: 'middle' };
  };

  return (
    <div className="flex h-screen bg-gray-50">
      <Sidebar />
      <div className="flex-1 overflow-auto">
        <div className="p-8">
          <div className="flex justify-between items-center mb-6">
            <div>
              <h1 className="text-2xl font-bold text-gray-800">셀렉실 관리</h1>
              <p className="text-sm text-gray-600 mt-1">셀렉실 사용 현황 및 예약 관리</p>
            </div>
            <div className="flex gap-3">
              <button onClick={() => setShowAddRoomModal(true)} className="bg-teal-600 text-white px-5 py-2.5 rounded-lg hover:bg-teal-700 transition-colors whitespace-nowrap flex items-center gap-2 cursor-pointer">
                <i className="ri-add-line"></i> 셀렉실 추가
              </button>
              <button onClick={() => setShowReservationModal(true)} className="bg-blue-600 text-white px-5 py-2.5 rounded-lg hover:bg-blue-700 transition-colors whitespace-nowrap flex items-center gap-2 cursor-pointer">
                <i className="ri-calendar-line"></i> 예약 등록
              </button>
            </div>
          </div>

          <div className="flex gap-2 mb-6 border-b border-gray-200">
            <button onClick={() => setActiveTab('reservations')} className={`px-6 py-3 font-medium transition-colors cursor-pointer whitespace-nowrap ${activeTab === 'reservations' ? 'text-blue-600 border-b-2 border-blue-600' : 'text-gray-600 hover:text-gray-800'}`}>예약 타임테이블</button>
            <button onClick={() => setActiveTab('rooms')} className={`px-6 py-3 font-medium transition-colors cursor-pointer whitespace-nowrap ${activeTab === 'rooms' ? 'text-blue-600 border-b-2 border-blue-600' : 'text-gray-600 hover:text-gray-800'}`}>셀렉실 현황</button>
          </div>

          {activeTab === 'rooms' && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {rooms.map(room => (
                <div key={room.id} className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow">
                  <div className="flex justify-between items-start mb-4">
                    <div>
                      <h3 className="text-lg font-bold text-gray-800">{room.name}</h3>
                      <p className="text-sm text-gray-600 mt-1">수용 인원: {room.capacity}명</p>
                    </div>
                    <button onClick={() => deleteRoom(room.id)} className="text-red-600 hover:text-red-700 cursor-pointer">
                      <i className="ri-delete-bin-line text-lg"></i>
                    </button>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className={`px-3 py-1.5 rounded-full text-sm font-medium ${getStatusColor(room.status)}`}>
                      {room.status === 'available' ? '사용 가능' : room.status === 'occupied' ? '사용 중' : '점검 중'}
                    </span>
                  </div>
                  <div className="mt-4 pt-4 border-t border-gray-100">
                    <p className="text-xs text-gray-500 mb-2">오늘의 예약</p>
                    <div className="text-sm text-gray-700 mb-1">
                      <span className="font-medium">예약 건수:</span> {room.todayReservationCount || 0}건
                    </div>
                    {room.nextReservationTime && (
                      <div className="text-sm text-blue-600">다음 예약: {parseTimeFromDt(room.nextReservationTime)}</div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}

          {activeTab === 'reservations' && (
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">날짜 선택</label>
                <input type="date" value={selectedDate} onChange={(e) => setSelectedDate(e.target.value)}
                  className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" />
              </div>
              <div className="overflow-x-auto">
                <table className="w-full border-collapse">
                  <thead>
                    <tr>
                      <th className="border border-gray-200 bg-gray-50 px-4 py-3 text-left text-sm font-semibold text-gray-700 sticky left-0 z-10 min-w-[80px]">시간</th>
                      {rooms.map(room => (
                        <th key={room.id} className="border border-gray-200 bg-gray-50 px-4 py-3 text-center text-sm font-semibold text-gray-700 min-w-[180px]">{room.name}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {timeSlots.map(time => {
                      const currentHour = getHour(time);
                      return (
                        <tr key={time}>
                          <td className="border border-gray-200 px-4 py-3 text-sm font-medium text-gray-700 bg-gray-50 sticky left-0 z-10 h-16 align-top">{time}</td>
                          {rooms.map(room => {
                            const info = getReservationInfo(room.id, currentHour);
                            if (info.type === 'middle') return null;
                            if (info.type === 'start' && info.reservation) {
                              return (
                                <td key={room.id} rowSpan={info.span}
                                  className="border border-gray-200 p-1 align-top relative cursor-pointer hover:opacity-90 transition-opacity"
                                  onClick={() => handleReservationClick(info.reservation!)}>
                                  <div className="bg-blue-50 border-l-4 border-blue-500 rounded p-2 h-full absolute inset-1 overflow-hidden shadow-sm">
                                    <p className="font-bold text-blue-900 text-sm truncate">{info.reservation.customerName}</p>
                                    <p className="text-xs text-blue-700 mt-1 truncate font-medium">{info.reservation.startTime} - {info.reservation.endTime}</p>
                                    <p className="text-xs text-gray-600 mt-1 truncate">{info.reservation.purpose}</p>
                                  </div>
                                </td>
                              );
                            }
                            return <td key={room.id} className="border border-gray-200 p-2 h-16"></td>;
                          })}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </div>

      {showAddRoomModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-6">
            <h2 className="text-xl font-bold text-gray-800 mb-4">셀렉실 추가</h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">셀렉실 이름</label>
                <input type="text" value={newRoom.name} onChange={(e) => setNewRoom({ ...newRoom, name: e.target.value })} className="w-full px-4 py-2 border border-gray-300 rounded-lg" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">수용 인원</label>
                <input type="number" value={newRoom.capacity} onChange={(e) => setNewRoom({ ...newRoom, capacity: parseInt(e.target.value) || 0 })} className="w-full px-4 py-2 border border-gray-300 rounded-lg" />
              </div>
            </div>
            <div className="flex gap-3 mt-6">
              <button onClick={() => setShowAddRoomModal(false)} className="flex-1 px-4 py-2.5 border rounded-lg">취소</button>
              <button onClick={handleAddRoom} className="flex-1 px-4 py-2.5 bg-teal-600 text-white rounded-lg">추가</button>
            </div>
          </div>
        </div>
      )}

      {showReservationModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-6 max-h-[90vh] overflow-y-auto">
            <h2 className="text-xl font-bold text-gray-800 mb-4">예약 등록</h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">셀렉실 선택<span className="text-red-500">*</span></label>
                <select value={newReservation.roomId} onChange={(e) => setNewReservation({ ...newReservation, roomId: e.target.value })} className="w-full px-4 py-2 border border-gray-300 rounded-lg">
                  <option value="">선택하세요</option>
                  {rooms.map(room => <option key={room.id} value={room.id}>{room.name}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">고객명<span className="text-red-500">*</span></label>
                <input type="text" value={newReservation.customerName} onChange={(e) => setNewReservation({ ...newReservation, customerName: e.target.value })} className="w-full px-4 py-2 border border-gray-300 rounded-lg" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">담당자<span className="text-red-500">*</span></label>
                <select value={newReservation.staffIdx} onChange={(e) => {
                  const id = Number(e.target.value);
                  const staff = staffList.find(s => s.userIdx === id);
                  setNewReservation({ ...newReservation, staffIdx: id, consultant: staff ? staff.name : '' });
                }} className="w-full px-4 py-2 border border-gray-300 rounded-lg cursor-pointer">
                  <option value={0}>담당 직원을 선택하세요</option>
                  {staffList.map((staff) => <option key={staff.userIdx} value={staff.userIdx}>{staff.name}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">날짜<span className="text-red-500">*</span></label>
                <input type="date" value={newReservation.date} onChange={(e) => setNewReservation({ ...newReservation, date: e.target.value })} className="w-full px-4 py-2 border border-gray-300 rounded-lg" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">시작 시간<span className="text-red-500">*</span></label>
                  <input type="time" value={newReservation.startTime} onChange={(e) => setNewReservation({ ...newReservation, startTime: e.target.value })} className="w-full px-4 py-2 border border-gray-300 rounded-lg" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">종료 시간<span className="text-red-500">*</span></label>
                  <input type="time" value={newReservation.endTime} onChange={(e) => setNewReservation({ ...newReservation, endTime: e.target.value })} className="w-full px-4 py-2 border border-gray-300 rounded-lg" />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">일정 유형<span className="text-red-500">*</span></label>
                <select value={newReservation.scheduleCategory} onChange={(e) => setNewReservation({ ...newReservation, scheduleCategory: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg">
                  {SELECT_CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">메모</label>
                <input type="text" value={newReservation.purpose} onChange={(e) => setNewReservation({ ...newReservation, purpose: e.target.value })} className="w-full px-4 py-2 border border-gray-300 rounded-lg" />
              </div>
            </div>
            <div className="flex gap-3 mt-6">
              <button onClick={() => setShowReservationModal(false)} className="flex-1 px-4 py-2.5 border rounded-lg">취소</button>
              <button onClick={handleAddReservation} className="flex-1 px-4 py-2.5 bg-blue-600 text-white rounded-lg">등록</button>
            </div>
          </div>
        </div>
      )}

      {showDetailModal && selectedResDetail && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-sm overflow-hidden">
            <div className="bg-blue-600 px-6 py-4 flex justify-between items-center">
              <h3 className="text-white font-bold text-lg">예약 상세 정보</h3>
              <button onClick={() => setShowDetailModal(false)} className="text-white hover:bg-blue-700 rounded-full p-1 transition-colors">
                <i className="ri-close-line text-xl"></i>
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div className="flex items-center gap-3 pb-4 border-b border-gray-100">
                <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center text-blue-600 text-xl font-bold">
                  {selectedResDetail.customerName.charAt(0).toUpperCase()}
                </div>
                <div>
                  <p className="text-lg font-bold text-gray-800">{selectedResDetail.customerName} 고객님</p>
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">{selectedResDetail.status}</span>
                </div>
              </div>
              <div className="space-y-3 text-sm">
                <div className="flex"><span className="w-24 text-gray-500">셀렉실</span><span className="font-medium text-gray-800">{selectedResDetail.roomName}</span></div>
                <div className="flex"><span className="w-24 text-gray-500">날짜</span><span className="font-medium text-gray-800">{selectedResDetail.date}</span></div>
                <div className="flex"><span className="w-24 text-gray-500">시간</span><span className="font-medium text-gray-800">{selectedResDetail.startTime} ~ {selectedResDetail.endTime}</span></div>
                <div className="flex"><span className="w-24 text-gray-500">고객명</span><span className="font-medium text-gray-800">{selectedResDetail.customerName}</span></div>
                <div className="flex"><span className="w-24 text-gray-500">담당자</span><span className="font-medium text-gray-800">{selectedResDetail.staffName || '-'}</span></div>
                <div className="flex"><span className="w-24 text-gray-500">메모</span><span className="font-medium text-gray-800">{selectedResDetail.purpose}</span></div>
              </div>
              <div className="pt-4 mt-2 flex gap-2 border-t border-gray-100">
                <button onClick={handleDeleteReservation}
                  className="px-4 py-2 bg-red-50 text-red-600 rounded-lg hover:bg-red-100 flex items-center justify-center gap-1 transition-colors">
                  <i className="ri-delete-bin-line"></i> 삭제
                </button>
                <button onClick={() => setShowDetailModal(false)}
                  className="flex-1 bg-gray-100 text-gray-700 py-2 rounded-lg hover:bg-gray-200 transition-colors">닫기</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
